import type { SalesforcePaymentsExpressConfig } from '../../types/config';
import type { PaymentIntentRequest } from '../../types/paymentRequest';
import type { PaymentMode, VendorAccount } from '../../types/paymentMethodSet';
import { PaymentMethodType } from '../../types/paymentMethodSet';
import { PaymentMethodElement } from '../../types/paymentMethodSet';
import './adyen-styles.css';
import type { VendorPaymentMethodElement } from '../../types/component';
import { SfpAdyenExpressComponent } from './sfp-adyen-express-component';
import { DEFAULT_LABELS } from '../../util/utility';
import type { MetadataReader } from '../../util/metadata-reader';
import type { SfPaymentMethodComponentConstructor } from '../sfp-payment-method-component';
import type { Component } from '../../components/component';

export class SfpAdyenExpressGooglePay extends SfpAdyenExpressComponent {
  TYPE = PaymentMethodType.GOOGLEPAY;
  ELEMENT = PaymentMethodElement.ADYEN_EXPRESS_GOOGLEPAY;

  onRender: (types: PaymentMethodType[]) => void;

  generateConfiguration(
    config: SalesforcePaymentsExpressConfig,
    paymentRequest: PaymentIntentRequest,
    vendorAccount: VendorAccount
  ): any {
    const initialTransactionInfo = {
      countryCode: vendorAccount.config.country.toUpperCase(),
      currencyCode: paymentRequest.currency.toUpperCase(),
      totalPriceStatus: 'FINAL',
      totalPrice: String(paymentRequest.amount),
      displayItems: [
        {
          label: config.labels?.productAmountTitle ?? DEFAULT_LABELS.productAmountTitle,
          type: 'SUBTOTAL',
          price: String(paymentRequest.amount),
        },
      ],
      totalPriceLabel: config.labels?.finalAmountTitle ?? DEFAULT_LABELS.finalAmountTitle,
    };

    const configuration = {
      shippingAddressRequired: !!config.options.shippingAddressRequired,
      shippingOptionRequired: !!config.options.shippingAddressRequired,
      emailRequired: !!config.options.emailAddressRequired,
      billingAddressRequired: config.options.billingAddressRequired ?? true,
      billingAddressParameters: {
        format: 'FULL',
        phoneNumberRequired: !!config.options.phoneNumberRequired,
      },

      transactionInfo: initialTransactionInfo,

      onClick: async (resolve: any): Promise<any> => {
        await config.actions.onClick(this.TYPE, this.paymentMode);
        resolve();
      },

      onError: (_error: any): any => {
        const errorEvent = new CustomEvent('sfp:paymentcancel', {
          bubbles: true,
          composed: true,
        });
        this.element.dispatchEvent(errorEvent);
      },

      onAuthorized: (paymentData: any): any => {
        this.billingDetails = {
          email: paymentData.email,
          phone: paymentData.paymentMethodData.info.billingAddress?.phoneNumber,
          name: paymentData.paymentMethodData.info.billingAddress?.name,
          address: {
            country: paymentData.paymentMethodData.info.billingAddress?.countryCode,
            city: paymentData.paymentMethodData.info.billingAddress?.locality,
            line1: paymentData.paymentMethodData.info.billingAddress?.address1,
            line2: paymentData.paymentMethodData.info.billingAddress?.address2,
            postalCode: paymentData.paymentMethodData.info.billingAddress?.postalCode,
            state: paymentData.paymentMethodData.info.billingAddress?.administrativeArea,
          },
        };

        this.shippingDetails = {
          name: paymentData.shippingAddress?.name,
          address: {
            country: paymentData.shippingAddress?.countryCode,
            city: paymentData.shippingAddress?.locality,
            line1: paymentData.shippingAddress?.address1,
            line2: paymentData.shippingAddress?.address2,
            postalCode: paymentData.shippingAddress?.postalCode,
            state: paymentData.shippingAddress?.administrativeArea,
          },
        };

        this.paymentResolve(true);
      },

      buttonType: config.theme?.expressButtons?.buttonLabels?.googlepay,
      buttonColor: config.theme?.expressButtons?.buttonColors?.googlepay,
      buttonLocale: paymentRequest.locale,
      buttonSizeMode: 'fill',
    };

    if (config.options.shippingAddressRequired) {
      return {
        ...configuration,
        callbackIntents: ['SHIPPING_ADDRESS', 'SHIPPING_OPTION'],
        // Callbacks
        paymentDataCallbacks: {
          onPaymentDataChanged(intermediatePaymentData: any): any {
            return new Promise((resolve, reject) => {
              const { callbackTrigger, shippingAddress, shippingOptionData } = intermediatePaymentData;
              if (callbackTrigger === 'INITIALIZE' || callbackTrigger === 'SHIPPING_ADDRESS') {
                const processedShippingAddress = {
                  city: shippingAddress.locality,
                  country: shippingAddress.countryCode,
                  postal_code: shippingAddress.postalCode,
                  state: shippingAddress.administrativeArea,
                };
                const callBackFunctionAddress = {
                  updateShippingAddress: (updatedData: any): any => {
                    if (updatedData.errors) {
                      resolve({
                        error: {
                          reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
                          message:
                            config.labels?.shippingAddressUnserviceable ?? DEFAULT_LABELS.shippingAddressUnserviceable,
                          intent: 'SHIPPING_ADDRESS',
                        },
                      });
                      return;
                    }

                    const shippingMethods: unknown[] = [];
                    const lineItems: unknown[] = [];

                    const amountToUpdate = String(updatedData.total);

                    if (updatedData.shippingMethods && updatedData.shippingMethods.length > 0) {
                      updatedData.shippingMethods.forEach((option: any) => {
                        const shippingMethod: unknown = {
                          id: option.id,
                          label: option.name,
                        };
                        shippingMethods.push(shippingMethod);
                      });
                    }
                    if (updatedData.lineItems && updatedData.lineItems.length > 0) {
                      updatedData.lineItems.forEach((lineItem: any) => {
                        const item: unknown = {
                          label: lineItem.name,
                          price: String(lineItem.amount),
                          type: 'SUBTOTAL',
                        };
                        lineItems.push(item);
                      });
                    }

                    const transactionInfo = {
                      countryCode: vendorAccount.config.country.toUpperCase(),
                      currencyCode: paymentRequest.currency.toUpperCase(),
                      totalPriceStatus: 'FINAL',
                      totalPrice: amountToUpdate,
                      displayItems: lineItems,
                      totalPriceLabel: config.labels?.finalAmountTitle ?? DEFAULT_LABELS.finalAmountTitle,
                    };

                    try {
                      // call event.resolve within 20 seconds
                      resolve({
                        newShippingOptionParameters: { shippingOptions: shippingMethods },
                        newTransactionInfo: transactionInfo,
                      });
                    } catch (e) {
                      //do nothing until shipping is handled
                      console.log(e);
                      reject();
                    }
                  },
                };
                config.actions.onShippingAddressChange(processedShippingAddress, callBackFunctionAddress);
              }
              if (callbackTrigger === 'SHIPPING_OPTION') {
                const processedShippingRate = {
                  id: shippingOptionData.id,
                };
                const callBackFunction = {
                  updateShippingMethod: (updatedData: any): any => {
                    if (updatedData.errors) {
                      resolve({
                        error: {
                          reason: 'SHIPPING_OPTION_INVALID',
                          message:
                            config.labels?.shippingOptionUnserviceable ?? DEFAULT_LABELS.shippingOptionUnserviceable,
                          intent: 'SHIPPING_OPTION',
                        },
                      });
                      return;
                    }

                    // should be called only before event is resolved
                    const shippingMethods: unknown[] = [];
                    const lineItems: unknown[] = [];

                    const amountToUpdate = String(updatedData.total);

                    if (updatedData.shippingMethods && updatedData.shippingMethods.length > 0) {
                      updatedData.shippingMethods.forEach((option: any) => {
                        const shippingMethod: unknown = {
                          id: option.id,
                          label: option.name,
                        };
                        shippingMethods.push(shippingMethod);
                      });

                      const index = updatedData.shippingMethods.findIndex(
                        (x: { name: any }) => x.name === updatedData.selectedShippingMethod.name
                      );
                      shippingMethods.splice(0, 0, shippingMethods.splice(index, 1)[0]);
                    }

                    if (updatedData.lineItems && updatedData.lineItems.length > 0) {
                      updatedData.lineItems.forEach((lineItem: any) => {
                        const item: unknown = {
                          label: lineItem.name,
                          price: String(lineItem.amount),
                          type: 'SUBTOTAL',
                        };
                        lineItems.push(item);
                      });
                    }

                    const transactionInfo = {
                      countryCode: vendorAccount.config.country.toUpperCase(),
                      currencyCode: paymentRequest.currency.toUpperCase(),
                      totalPriceStatus: 'FINAL',
                      totalPrice: amountToUpdate,
                      displayItems: lineItems,
                      totalPriceLabel: config.labels?.finalAmountTitle ?? DEFAULT_LABELS.finalAmountTitle,
                    };

                    try {
                      // call event.resolve within 20 seconds
                      resolve({
                        newShippingOptionParameters: { shippingOptions: shippingMethods },
                        newTransactionInfo: transactionInfo,
                      });
                    } catch (e) {
                      //do nothing until shipping is handled
                      console.log(e);
                      reject();
                    }
                  },
                };
                config.actions.onShippingMethodChange(processedShippingRate, callBackFunction);
              }
            });
          },
        },
      };
    }

    return configuration;
  }
}

export const createSfpAdyenExpressGooglePay: SfPaymentMethodComponentConstructor = function (
  parentComponent: Component,
  vendorObject: any,
  elements: any,
  metadataReader: MetadataReader,
  config: SalesforcePaymentsExpressConfig,
  paymentRequest: PaymentIntentRequest,
  paymentMethod: VendorPaymentMethodElement,
  paymentMode?: PaymentMode
) {
  return new SfpAdyenExpressGooglePay(
    parentComponent,
    vendorObject,
    elements,
    metadataReader,
    config,
    paymentRequest,
    paymentMethod,
    paymentMode
  );
};
